import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Download, FileText, Loader2 } from 'lucide-react';

export interface ReportFormatOption {
  /** Stable key — drives the icon color below. Use 'pdf' | 'csv' | 'json' | 'excel' when
   *  possible so the color stays consistent with every other ReportButton in the app. */
  key: string;
  /** Dropdown item label, e.g. "Exportar PDF". Ignored when there's only one format — see below. */
  label: string;
  onSelect: () => void | Promise<void>;
}

interface ReportButtonProps {
  /** Formats this particular report actually supports — NOT a fixed PDF+CSV pair. A single
   *  entry renders as a plain one-click button (no pointless one-item dropdown); 2+ renders
   *  the standard "Relatório ▾" dropdown. */
  formats: ReportFormatOption[];
  loading?: boolean;
  disabled?: boolean;
  label?: string;
  title?: string;
  className?: string;
  /** For the handful of genuinely dark-themed embedded panels (not a "light/dark mode toggle"
   *  — an intentionally dark operational view) — same behavior, dark-appropriate colors. */
  dark?: boolean;
}

const FORMAT_COLOR: Record<string, string> = {
  pdf: 'text-rose-500',
  csv: 'text-emerald-500',
  json: 'text-blue-500',
  excel: 'text-emerald-600',
};

// Single standardized "generate a report" trigger — a button that opens a small dropdown
// listing only the export formats a given report actually supports. Every page that offers
// report export should render this instead of its own bespoke button+dropdown/modal, so the
// look, wording ("Relatório"), and behavior stay identical everywhere in the app.
export function ReportButton({
  formats,
  loading = false,
  disabled = false,
  label = 'Relatório',
  title = 'Gerar relatório',
  className,
  dark = false,
}: ReportButtonProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (formats.length === 0) return null;

  const triggerClass = dark
    ? 'inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium text-gray-300 hover:text-white ' +
      'bg-transparent hover:bg-gray-800 border border-gray-700 rounded-lg transition-colors ' +
      'disabled:opacity-40 disabled:cursor-not-allowed'
    : 'inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium text-gray-600 bg-white border ' +
      'border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors ' +
      'disabled:opacity-40 disabled:cursor-not-allowed';

  const menuClass = dark
    ? 'absolute right-0 top-full mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-20 overflow-hidden w-40 py-1'
    : 'absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-20 overflow-hidden w-40 py-1';

  const itemClass = dark
    ? 'w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-gray-300 hover:bg-gray-800 transition-colors'
    : 'w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors';

  // Exactly one format — no dropdown needed, just a plain button that fires it directly.
  if (formats.length === 1) {
    const only = formats[0];
    return (
      <button
        onClick={() => only.onSelect()}
        disabled={disabled || loading}
        title={title}
        className={`${triggerClass} ${className || ''}`}
      >
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
        {label}
      </button>
    );
  }

  return (
    <div className={`relative ${className || ''}`} ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        disabled={disabled || loading}
        title={title}
        className={triggerClass}
      >
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
        {label}
        <ChevronDown className={`h-3 w-3 ${dark ? 'text-gray-500' : 'text-gray-400'}`} />
      </button>
      {open && (
        <div className={menuClass}>
          {formats.map(f => (
            <button key={f.key} onClick={() => { setOpen(false); f.onSelect(); }} className={itemClass}>
              {/* Compound keys like "pdf-completo" (one report offered at several scopes) still get
                the format's colour — the lookup falls back to the part before the first dash. */}
            <Download className={`w-3.5 h-3.5 ${FORMAT_COLOR[f.key] || FORMAT_COLOR[f.key.split('-')[0]] || 'text-gray-500'}`} />
              {f.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
